<template>
  <div class="grid grid-cols-1 gap-x-6 px-1.5 md:grid-cols-2">
    <div>
      <form.Field name="title" v-slot="{ field }">
        <SField :data-cy="'customer-field-' + field.name" class="mb-4">
          <SFieldLabel>{{ $t("customer.person-status") }}</SFieldLabel>
          <SelectPersonStatus
            :model-value="field.state.value"
            @update:model-value="field.handleChange"
          />
        </SField>
      </form.Field>

      <form.Field name="firstName" v-slot="{ field }">
        <SField
          :data-cy="'customer-field-' + field.name"
          :data-invalid="showError(field) || undefined"
          class="mb-4"
        >
          <SFieldLabel :for="field.name">
            {{ $t("customer.first-name") }}
            <span class="text-destructive" aria-hidden="true">*</span>
          </SFieldLabel>
          <SInput
            :id="field.name"
            :model-value="field.state.value"
            :aria-invalid="showError(field) || undefined"
            @update:model-value="field.handleChange"
            @blur="field.handleBlur"
          />
          <SFieldError v-if="showError(field)">{{
            firstErrorText(field)
          }}</SFieldError>
        </SField>
      </form.Field>

      <form.Field name="lastName" v-slot="{ field }">
        <SField
          :data-cy="'customer-field-' + field.name"
          :data-invalid="showError(field) || undefined"
          class="mb-4"
        >
          <SFieldLabel :for="field.name">
            {{ $t("customer.last-name") }}
            <span class="text-destructive" aria-hidden="true">*</span>
          </SFieldLabel>
          <SInput
            :id="field.name"
            :model-value="field.state.value"
            :aria-invalid="showError(field) || undefined"
            @update:model-value="field.handleChange"
            @blur="field.handleBlur"
          />
          <SFieldError v-if="showError(field)">{{
            firstErrorText(field)
          }}</SFieldError>
        </SField>
      </form.Field>

      <form.Field name="email" v-slot="{ field }">
        <SField
          :data-cy="'customer-field-' + field.name"
          :data-invalid="showError(field) || undefined"
          class="mb-4"
        >
          <SFieldLabel :for="field.name">
            {{ $t("customer.email") }}
            <span class="text-destructive" aria-hidden="true">*</span>
          </SFieldLabel>
          <SInput
            :id="field.name"
            type="email"
            :model-value="field.state.value"
            :aria-invalid="showError(field) || undefined"
            @update:model-value="(v) => field.handleChange(String(v).trim())"
            @blur="field.handleBlur"
          />
          <SFieldError v-if="showError(field)">{{
            firstErrorText(field)
          }}</SFieldError>
        </SField>
      </form.Field>

      <form.Field name="language" v-slot="{ field }">
        <SField
          :data-cy="'customer-field-' + field.name"
          :data-invalid="showError(field) || undefined"
          class="mb-4"
        >
          <SFieldLabel>{{ $t("customer.language") }}</SFieldLabel>
          <SelectLanguages
            :model-value="field.state.value"
            @update:model-value="field.handleChange"
          />
          <SFieldError v-if="showError(field)">{{
            firstErrorText(field)
          }}</SFieldError>
        </SField>
      </form.Field>

      <form.Field name="birthDate" v-slot="{ field }">
        <SField :data-cy="'customer-field-' + field.name" class="mb-4">
          <SFieldLabel>{{ $t("customer.birthdate") }}</SFieldLabel>
          <SDatePicker
            :model-value="
              field.state.value ? parseDate(field.state.value) : undefined
            "
            display-format="YYYY-MM-DD"
            trigger-class="w-full"
            :placeholder="$t('customer.birthdate-placeholder')"
            @update:model-value="
              (v) => field.handleChange(v ? v.toString() : '')
            "
          />
        </SField>
      </form.Field>

      <form.Field name="emailMarketingOrganiser" v-slot="{ field }">
        <SField :data-cy="'customer-field-' + field.name" class="mb-4">
          <SFieldLabel>{{
            $t("customer.email-marketing-organiser")
          }}</SFieldLabel>
          <SSelect
            :model-value="field.state.value"
            @update:model-value="field.handleChange"
          >
            <SSelectTrigger class="w-full">
              <SSelectValue :placeholder="$t('button.select')" />
            </SSelectTrigger>
            <SSelectContent>
              <SSelectItem
                v-for="item in GET_YES_NO_CONTENT"
                :key="item.value"
                :value="item.value"
              >
                {{ item.label }}
              </SSelectItem>
            </SSelectContent>
          </SSelect>
        </SField>
      </form.Field>
    </div>

    <div>
      <form.Field name="allergies" v-slot="{ field }">
        <SField :data-cy="'customer-field-' + field.name" class="mb-4">
          <SFieldLabel :for="field.name">{{
            $t("customer.allergies")
          }}</SFieldLabel>
          <SInput
            :id="field.name"
            :model-value="field.state.value"
            @update:model-value="field.handleChange"
          />
        </SField>
      </form.Field>

      <form.Field name="address" v-slot="{ field }">
        <SField :data-cy="'customer-field-' + field.name" class="mb-4">
          <SFieldLabel :for="field.name">{{
            $t("customer.address")
          }}</SFieldLabel>
          <SInput
            :id="field.name"
            :model-value="field.state.value"
            @update:model-value="field.handleChange"
          />
        </SField>
      </form.Field>

      <form.Field name="zipCode" v-slot="{ field }">
        <SField
          :data-cy="'customer-field-' + field.name"
          :data-invalid="showError(field) || undefined"
          class="mb-4"
        >
          <SFieldLabel :for="field.name">{{ $t("customer.zip") }}</SFieldLabel>
          <SInput
            :id="field.name"
            :model-value="field.state.value"
            :aria-invalid="showError(field) || undefined"
            @update:model-value="field.handleChange"
            @blur="field.handleBlur"
          />
          <SFieldError v-if="showError(field)">{{
            firstErrorText(field)
          }}</SFieldError>
        </SField>
      </form.Field>

      <form.Field name="city" v-slot="{ field }">
        <SField
          :data-cy="'customer-field-' + field.name"
          :data-invalid="showError(field) || undefined"
          class="mb-4"
        >
          <SFieldLabel :for="field.name">{{ $t("customer.city") }}</SFieldLabel>
          <SInput
            :id="field.name"
            :model-value="field.state.value"
            :aria-invalid="showError(field) || undefined"
            @update:model-value="field.handleChange"
            @blur="field.handleBlur"
          />
          <SFieldError v-if="showError(field)">{{
            firstErrorText(field)
          }}</SFieldError>
        </SField>
      </form.Field>

      <form.Field name="country" v-slot="{ field }">
        <SField :data-cy="'customer-field-' + field.name" class="mb-4">
          <SFieldLabel>{{ $t("customer.country") }}</SFieldLabel>
          <SCountrySelect
            :model-value="field.state.value"
            :lang="AppModule.language"
            @update:model-value="field.handleChange"
          />
        </SField>
      </form.Field>

      <form.Field name="company" v-slot="{ field }">
        <SField
          :data-cy="'customer-field-' + field.name"
          :data-invalid="showError(field) || undefined"
          class="mb-4"
        >
          <SFieldLabel :for="field.name">{{
            $t("customer.company")
          }}</SFieldLabel>
          <SInput
            :id="field.name"
            :model-value="field.state.value"
            :aria-invalid="showError(field) || undefined"
            @update:model-value="field.handleChange"
            @blur="field.handleBlur"
          />
          <SFieldError v-if="showError(field)">{{
            firstErrorText(field)
          }}</SFieldError>
        </SField>
      </form.Field>

      <form.Field name="phone" v-slot="{ field }">
        <SField :data-cy="'customer-field-' + field.name" class="mb-4">
          <SFieldLabel>{{ $t("columns.phone-number") }}</SFieldLabel>
          <MazPhoneNumberInput
            :model-value="field.state.value"
            color="info"
            show-code-on-list
            :default-country-code="form.state.values.country"
            list-position="top left"
            :success="results?.isValid"
            size="md"
            @update:model-value="field.handleChange"
            @update="results = $event"
          />
        </SField>
      </form.Field>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { parseDate } from "@internationalized/date";
import MazPhoneNumberInput from "maz-ui/components/MazPhoneNumberInput";

import {
  SInput,
  SField,
  SFieldLabel,
  SFieldError,
  SSelect,
  SSelectContent,
  SSelectItem,
  SSelectTrigger,
  SSelectValue,
} from "@smeetz/ui-core";
import { SDatePicker } from "@smeetz/ui-components/s-date-picker";

import { useSmeetzForm } from "@smeetz/services-validation";

import POSConfigModule from "@/store/modules/pos-config";
import CustomersModule from "@/store/modules/customers";
import { convertCodeToIdLang, showLanguagesList } from "@/utils/helpers/lang";

import SelectPersonStatus from "@/components/form-items/selects/select-person-status/SelectPersonStatus.vue";
import SelectLanguages from "@/components/form-items/selects/select-languages/SelectLanguages.vue";
import { SCountrySelect } from "@smeetz/ui-components/s-country-select";
import AppModule from "@/store/modules/app";

import {
  customerEditSchema,
  type CustomerEditValues,
} from "./customer-edit.form";

const { t } = useI18n({ useScope: "global" });

const results = ref<{ isValid?: boolean }>();

const GET_YES_NO_CONTENT = computed(() => [
  { value: 0, label: t("common.no") },
  { value: 1, label: t("common.yes") },
]);

const editedCustomer = CustomersModule.editedCustomer[0];

const fullName: Record<string, string> = (editedCustomer.fullName ?? "")
  .split(" ")
  .reduce(
    (acc: Record<string, string>, part: string, index: number) => ({
      ...acc,
      [`name${index + 1}`]: part,
    }),
    {}
  );

const groupBranding = POSConfigModule.groupInfo?.brandingList;
const groupCountry = groupBranding?.[0]?.country?.alpha2 ?? "CH";

// Supplied language can be a language id or code, and elasticsearch omits it for
// some customers. The language select and the save mapping both work with ids,
// so resolve to an id, falling back to the app language (like the create form)
const knownLanguage = showLanguagesList().find(
  (item) =>
    String(item.id) === String(editedCustomer.language) ||
    item.code === editedCustomer.language
);
const language = knownLanguage?.id ?? convertCodeToIdLang(AppModule.language);

const { form, showError, firstErrorText } = useSmeetzForm<CustomerEditValues>({
  defaultValues: {
    // id can arrive as a number from graphql (pulled orders); the schema and
    // the rest of the app treat it as a string, so normalise it here.
    id: editedCustomer.id ? String(editedCustomer.id) : "",
    title: editedCustomer.title || "",
    firstName: editedCustomer.firstName || fullName.name1 || "",
    lastName: editedCustomer.lastName || fullName.name2 || "",
    email: editedCustomer.email || "",
    language,
    // birthDate can arrive as a full ISO datetime (e.g. 1999-06-01T00:00:00+00:00),
    // but the date picker's parseDate only accepts a plain YYYY-MM-DD, so keep the date part.
    birthDate: (editedCustomer.birthDate || "").slice(0, 10),
    allergies: editedCustomer.allergies || "",
    address: editedCustomer.address || "",
    zipCode: editedCustomer.zipCode || "",
    city: editedCustomer.city || "",
    country: editedCustomer.country || groupCountry,
    phone: editedCustomer.phone || "",
    company: editedCustomer.company || "",
    emailMarketingOrganiser: editedCustomer.emailMarketingOrganiser ? 1 : 0,
  },
  schema: customerEditSchema(),
  validatorTiming: "onSubmit",
});

/**
 * Validate every field and report the result with the current values.
 *
 * @returns {Promise<{ valid: boolean; values: CustomerEditValues }>}
 */
const validate = async () => {
  await form.handleSubmit();
  return {
    valid: form.state.isValid,
    values: form.state.values as CustomerEditValues,
  };
};

defineExpose({ validate });
</script>
